// Helpers to keep the testimonial carousel's `loop: true` from silently
// disabling itself.
//
// embla only loops when it can build wrap-around clones — `canLoop()` requires
// `totalSlideWidth - oneSlide >= viewportWidth`. If false, embla recreates the
// engine with `loop: false` (no warning), so the strip scrolls to the end and
// stops, leaving blank space. The testimonial slides are a FIXED width, so with
// only a few cards on a wide screen the strip can't satisfy that — we fix it by
// repeating the cards enough times that the track always overflows the widest
// realistic viewport.

// Per-slide footprint in px. Tied to `.testimonialCarouselItem` in
// CardSection.module.css: 480px card + 2 * 24px horizontal padding = 528px at
// >=768px (the desktop/widescreen case, which is where looping fails). Keep in
// sync if that CSS rule changes.
export const SLIDE_W = 528;

// Widest viewport we guarantee a seamless loop for (≈ 4K). embla re-measures on
// resize, so sizing for this covers essentially every real display.
export const MAX_VIEWPORT = 3840;

// Hard ceiling on repetitions so a single card can't explode the DOM.
export const MAX_COPIES = 10;

/**
 * How many times to repeat the testimonial cards so embla can always loop.
 * Returns 1 when the cards already overflow the viewport (no duplication), and
 * 1 for empty input (the caller renders nothing).
 */
export function loopCopies(
  cardCount: number,
  slideWidth: number = SLIDE_W,
  maxViewport: number = MAX_VIEWPORT,
): number {
  if (cardCount <= 0) return 1;
  // canLoop needs total >= viewport + one slide; add one slide of headroom.
  const requiredTotal = maxViewport + slideWidth;
  const singleSet = cardCount * slideWidth;
  if (singleSet >= requiredTotal) return 1;
  return Math.min(MAX_COPIES, Math.ceil(requiredTotal / singleSet));
}

/**
 * Repeat `cards` `loopCopies(...)` times into a flat array for the looping
 * carousel. Order is preserved (cards 1..n, then 1..n again, ...).
 */
export function buildLoopCards<T>(cards: T[]): T[] {
  const copies = loopCopies(cards.length);
  if (copies <= 1) return cards;
  return Array.from({ length: copies }, () => cards).flat();
}
